// NOTE: Adding @sentry/react to the dependencies causes an error because of dependencies it installs.
// Our other sentry dependencies (@sentry/react-native) already install @sentry/react so we can ignore this error.
// eslint-disable-next-line import/no-extraneous-dependencies
import * as Sentry from "@sentry/react";
import platform from "platform-detect";
import { useState } from "react";
import { Linking } from "react-native";

interface UseExternalLinkResult {
  isLoading: string | null;
  error: string | null;
  openLink: (href: string) => Promise<void>;
}

// eslint-disable-next-line import/prefer-default-export
export const useExternalLink = (): UseExternalLinkResult => {
  const [isLoading, setIsLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const openLink = async (href: string): Promise<void> => {
    setIsLoading(href);
    setError(null);

    try {
      if (platform.pwa) {
        // Attempt to open the window
        const newWindow = window.open(href, "_blank", "noopener,noreferrer");

        // Check if window.open was successful
        if (!newWindow) {
          // On iOS PWAs, window.open() might return null even when working correctly
          const isIOS = platform.ios;

          if (isIOS) {
            console.log("iOS PWA detected - window.open() returned null but may still work");
            // We don't throw an error for iOS PWAs since this behavior is expected
          } else {
            const errorMessage = "Browser blocked opening the window or popup blocker is enabled";
            console.error(errorMessage);

            // Create a structured error for Sentry with context
            const sentryError = new Error(errorMessage);
            sentryError.name = "PWALinkOpenError";

            // Add contextual information to the error
            Sentry.withScope((scope) => {
              scope.setContext("link_details", {
                url: href,
                isPWA: platform.pwa,
                userAgent: navigator.userAgent,
              });
              Sentry.captureException(sentryError);
            });

            throw sentryError;
          }
        }

        // Check if the window was actually focused
        if (newWindow && (newWindow.closed || typeof newWindow.closed === "undefined")) {
          const focusError = new Error("Window was created but couldn't be focused or was immediately closed");
          Sentry.captureException(focusError);
          throw focusError;
        }
      } else {
        // Use Linking API for non-PWA context
        await Linking.openURL(href);
      }
    } catch (err) {
      console.error("Failed to open URL:", err);

      // Track all errors in Sentry
      Sentry.captureException(err);

      // Show user-friendly error message
      setError("Failed to open the link. Please try again.");
    } finally {
      setIsLoading(null);
    }
  };

  return { isLoading, error, openLink };
};
